import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Field, Input } from "@ect/ui";
import { useSession } from "../app/session";

/** The authenticator secret stays on this screen until a valid code confirms enrollment. */
export function SecuritySettings() {
  const { api } = useSession();
  const { t } = useTranslation();
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [secret, setSecret] = useState<string | null>(null);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    let active = true;
    void api.json<{ totp_enabled: boolean }>("/api/auth/me").then((u) => { if (active) setEnabled(u.totp_enabled); }).catch(() => { if (active) setError(t("security.loadError")); });
    return () => { active = false; };
  }, [api, t]);
  const enroll = async () => {
    setBusy(true); setError("");
    try {
      const result = await api.json<{ secret: string }>("/api/auth/totp/enroll", { method: "POST" });
      setSecret(result.secret); setCode("");
    } catch { setError(t("security.setupError")); }
    finally { setBusy(false); }
  };
  const activate = async () => {
    setBusy(true); setError("");
    try {
      await api.json<void>("/api/auth/totp/activate", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ code }) });
      setEnabled(true); setSecret(null); setCode("");
    } catch { setError(t("security.codeError")); }
    finally { setBusy(false); }
  };
  return <div className="grid gap-3 rounded-md border border-line bg-surface p-4">
    <div className="font-semibold">{t("security.title")}</div>
    <p className="text-sm text-ink-2">{t(enabled ? "security.enabled" : "security.description")}</p>
    {enabled === false && !secret && <Button size="sm" loading={busy} onClick={() => void enroll()}>{t("security.start")}</Button>}
    {secret && <>
      <p className="text-sm">{t("security.instructions")}</p>
      <Field id="totp-secret" label={t("security.secret")}><Input id="totp-secret" dir="ltr" readOnly value={secret} className="font-mono" /></Field>
      <p className="text-xs text-ink-2">{t("security.backup")}</p>
      <Field id="totp-code" label={t("login.totp")}><Input id="totp-code" dir="ltr" autoComplete="one-time-code" inputMode="numeric" maxLength={6} value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} /></Field>
      <Button size="sm" loading={busy} disabled={code.length !== 6} onClick={() => void activate()}>{t("security.confirm")}</Button>
    </>}
    {error && <p role="alert" className="text-sm text-danger">{error}</p>}
  </div>;
}
